import { useQuery } from "@tanstack/react-query";
import { getNationRivalry } from "@/lib/social.functions";
import { useLanguage } from "@/hooks/useLanguage";

const FLAGS: Record<string, string> = {
  MEX: "🇲🇽", ARG: "🇦🇷", BRA: "🇧🇷", FRA: "🇫🇷", ESP: "🇪🇸", USA: "🇺🇸",
  ENG: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", GER: "🇩🇪", ITA: "🇮🇹", POR: "🇵🇹", URU: "🇺🇾", COL: "🇨🇴",
  JPN: "🇯🇵", KOR: "🇰🇷", NED: "🇳🇱", BEL: "🇧🇪", CRO: "🇭🇷", MAR: "🇲🇦",
};

/**
 * HeatRivalryMini — 2-row leaderboard of fan support.
 * Reuses the existing React Query cache for ["social", "nation_rivalry"].
 */
export function HeatRivalryMini({ highlight }: { highlight?: string | null }) {
  const { t } = useLanguage();
  const q = useQuery({
    queryKey: ["social", "nation_rivalry"],
    queryFn: () => getNationRivalry(),
    refetchInterval: 30_000,
    staleTime: 20_000,
  });
  const rows = (q.data?.rows ?? []) as Array<{ nation_code: string; hype_total: number; active_fans: number }>;
  const ranked = rows
    .map((r) => ({ code: String(r.nation_code), total: Number(r.hype_total ?? 0), fans: Number(r.active_fans ?? 0) }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 2);
  if (ranked.length === 0) return null;
  const max = Math.max(1, ranked[0].total);
  return (
    <div className="rounded-lg border border-border/50 bg-background/40 p-2">
      <div className="mb-1.5 text-[10px] font-black uppercase tracking-wider text-muted-foreground">
        {t("fan_power_rivalry_title")}
      </div>
      <ul className="space-y-1.5">
        {ranked.map((r) => {
          const pct = Math.round((r.total / max) * 100);
          const isMe = highlight && r.code === highlight;
          return (
            <li key={r.code} className="space-y-0.5">
              <div className="flex items-center justify-between text-[11px] font-bold">
                <span className={isMe ? "text-primary" : ""}>
                  <span aria-hidden className="mr-1">{FLAGS[r.code] ?? "🏳️"}</span>
                  {r.code}{isMe ? " · tú" : ""}
                </span>
                <span className="font-mono tabular-nums text-muted-foreground">{r.total}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-secondary/60">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${pct}%`,
                    background: isMe
                      ? "linear-gradient(90deg, oklch(0.78 0.2 60), oklch(0.65 0.27 27))"
                      : "linear-gradient(90deg, oklch(0.7 0.05 250), oklch(0.6 0.1 250))",
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}